import React, { useState, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { z } from "zod";
import {
  Box,
  Typography,
  Alert,
  Divider,
  Button,
  TextField,
  Grid,
  CardContent,
  Card,
  Breadcrumbs,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import Map from "./selectLocationMap/Map";
import { useForm, Controller } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";

import { ROUTES } from "../../constants";
import { useWaterLevelRecordsForm } from "./useWaterLevelRecordsForm";
import { zodResolver } from "@hookform/resolvers/zod";
import { DatePicker } from "@mui/x-date-pickers";
import { format, parseISO } from "date-fns";
import { styled } from "@mui/material/styles";
import FetchElevationButton from "../../components/FetchElevationButton";
import CalculateWaterElevationButton from "../../components/CalculateWaterElevationButton";
import AdminVisibilityFilter from "../../components/AdminVisibilityFilter";

const oneDecimal = /^\d+\.\d{1}$/;

const waterLevelSchema = z.object({
  ndx: z.number().nullable(),
  date: z
    .date({
      required_error: "Please select a date",
      invalid_type_error: "Invalid date format",
    })
    .nullable()
    .refine((val) => val !== null, {
      message: "Date is required",
    }),
  time: z.string().optional().nullable(),
  land_surface_elevation_ftabsl: z
    .preprocess((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = typeof val === "string" ? parseFloat(val) : val;
      return Number.isNaN(num) ? undefined : num;
    }, z.union([z.number(), z.null(), z.undefined()]))
    .refine((val) => val !== undefined, {
      message: "Elevation must be a valid number",
    }),
  measuring_point_ft: z.number(),
  water_level_ft_below_mp: z
    .number()
    .refine((val) => oneDecimal.test(val.toString()), {
      message:
        "Must have exactly one digit after the decimal point (e.g., 3.5)",
    }),
  water_elevation_ft_absl: z.number().catch(null),
  measure_method_ndx: z.number().min(1, "Method is required"),
  comments: z.string().optional(),
  well_ndx: z.number().optional(),
});

const DEFAULT_FORM_VALUES = {
  ndx: null,
  date: new Date(),
  time: null,
  land_surface_elevation_ftabsl: null,
  measuring_point_ft: null,
  water_level_ft_below_mp: null,
  water_elevation_ft_absl: null,
  measure_method_ndx: 5,
  comments: "",
};

const PageContainer = styled(Box)`
  height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const waterLevelFields = [
  {
    value: "measuring_point_ft",
    label: "Measuring Point (ft)",
    required: true,
  },
  {
    value: "water_level_ft_below_mp",
    label: "Water Level Depth (ft.)",
    required: true,
  },
  {
    value: "water_elevation_ft_absl",
    label: "Water Elevation (ft above sea level)",
    required: false,
  },
];

export default function WaterLevelRecordsManagement() {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const wellNdx = parseInt(queryParams.get("well_ndx"), 10) || null;

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showAllWells, setShowAllWells] = useState(false);

  const {
    myWellsQuery,
    measurementMethodQuery,
    waterLevelEntriesQuery,
    saveWaterLevelMutation,
    deleteWaterLevelMutation,
    contactByWellQuery,
  } = useWaterLevelRecordsForm(wellNdx);

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, isDirty, errors },
  } = useForm({
    resolver: zodResolver(waterLevelSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const {
    data: wellsData = [],
    isLoading: isLoadingWells,
    error: errorWells,
  } = myWellsQuery;

  const filteredWells = useMemo(() => {
    if (showAllWells) return wellsData;
    return wellsData.filter((w) => w.is_permitted);
  }, [wellsData, showAllWells]);

  const {
    data: waterLevelsData = [],
    isLoading: isLoadingWaterLevels,
    error: errorWaterLevels,
  } = waterLevelEntriesQuery;

  const {
    data: measurementMethodsData = [],
    isLoading: isLoadingMethods,
    error: errorMethods,
  } = measurementMethodQuery;

  const {
    data: contactData = [],
    isLoading: isLoadingContact,
    isError: isErrorContact,
  } = contactByWellQuery;

  const loading =
    isLoadingWells ||
    isLoadingWaterLevels ||
    isLoadingMethods ||
    isLoadingContact;

  const error =
    errorWells || errorWaterLevels || errorMethods || isErrorContact;

  const columns = useMemo(() => {
    return [
      { field: "date", headerName: "Date", minWidth: 100 },
      { field: "time", headerName: "Time", minWidth: 80 },
      {
        field: "land_surface_elevation_ftabsl",
        headerName: "Land Surface (ft abv sea lvl)",
        minWidth: 120,
      },
      {
        field: "measuring_point_ft",
        headerName: "Measuring Point (ft)",
        minWidth: 120,
      },
      {
        field: "water_level_ft_below_mp",
        headerName: "Water Level Depth (ft.)",
        minWidth: 120,
      },
      {
        field: "water_elevation_ft_absl",
        headerName: "Water Elev. (ft abv sea lvl)",
        minWidth: 120,
      },
      {
        field: "measure_method_ndx",
        headerName: "Method",
        minWidth: 150,
        valueGetter: (value) => {
          const found = measurementMethodsData.find(
            (m) => m.measure_method_ndx === value
          );
          return found?.measure_method || "N/A";
        },
      },
      {
        field: "comments",
        headerName: "Comments",
        minWidth: 200,
      },
    ];
  }, [measurementMethodsData]);

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ display: "flex", alignItems: "center", p: 2 }}>
      <Box sx={{ mr: "auto", display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleEditRecord}
          disabled={!selectedRowId || isSubmitting || loading}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteRequest}
          disabled={!selectedRowId || isSubmitting || loading}
        >
          Delete
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleAddRecord}
          disabled={isSubmitting || loading || !wellNdx}
        >
          Add Water Level Record
        </Button>
        <GridToolbarExport
          slotProps={{
            button: {
              variant: "outlined",
              size: "medium",
            },
          }}
        />
      </Box>
    </GridToolbarContainer>
  );

  const setWellNdx = (newWellNdx) => {
    const searchParams = new URLSearchParams(location.search);
    if (newWellNdx) searchParams.set("well_ndx", newWellNdx);
    else searchParams.delete("well_ndx");
    history.push({ search: searchParams.toString() });
  };

  /* ------------------------------------------------------------------
     Handlers (Add, Edit, Delete)
  ------------------------------------------------------------------ */
  const handleWellChange = (_, newVal) => {
    if (newVal?.well_ndx) {
      setWellNdx(newVal.well_ndx);
    } else {
      setWellNdx(null);
    }
    setSelectedRowId(null);
  };

  const handleRowSelection = (newSelection) => {
    setSelectedRowId(newSelection.length > 0 ? newSelection[0] : null);
  };

  /** Add new row: open dialog in create mode */
  const handleAddRecord = () => {
    setIsCreateMode(true);
    reset({
      ...DEFAULT_FORM_VALUES,
      well_ndx: wellNdx || null, // Set the well_ndx when adding a new record
    });
    setOpenDialog(true);
  };

  /** Edit existing row: open dialog in edit mode */
  const handleEditRecord = () => {
    if (!selectedRowId || !waterLevelsData) return;
    const existing = waterLevelsData.find((row) => row.ndx === selectedRowId);
    if (!existing) return;

    setIsCreateMode(false);
    reset({
      ndx: selectedRowId,
      date: existing.date ? parseISO(existing.date) : null,
      time: existing.time || null,
      land_surface_elevation_ftabsl:
        +existing.land_surface_elevation_ftabsl || null,
      measuring_point_ft: +existing.measuring_point_ft || null,
      water_level_ft_below_mp: +existing.water_level_ft_below_mp || null,
      water_elevation_ft_absl: +existing.water_elevation_ft_absl || null,
      measure_method_ndx: existing.measure_method_ndx || null,
      comments: existing.comments || "",
      well_ndx: existing.well_ndx || wellNdx || null,
    });
    setOpenDialog(true);
  };

  /** Delete row: open confirm dialog */
  const handleDeleteRequest = () => {
    if (!selectedRowId) return;
    setDeleteDialogOpen(true);
  };

  /** Actually confirm Delete */
  const confirmDelete = async () => {
    if (!selectedRowId) return;
    try {
      await deleteWaterLevelMutation.mutateAsync(selectedRowId);
      setSelectedRowId(null);
    } catch (err) {
      console.error("Delete error:", err);
    }
    setDeleteDialogOpen(false);
  };

  /* ------------------------------------------------------------------
     Dialog Logic
  ------------------------------------------------------------------ */
  /** Submitting the Add/Edit dialog form */
  const onSubmitForm = async (formValues) => {
    try {
      // Convert dates to strings for API
      const payload = {
        ...formValues,
        date: formValues.date ? format(formValues.date, "yyyy-MM-dd") : null,
      };

      await saveWaterLevelMutation.mutateAsync(payload);
      handleCloseDialog();
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  /** Close the Add/Edit dialog */
  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset({});
  };

  if (error) {
    return <div>An unexpected error has occured: {String(error)}</div>;
  }

  return (
    <Box>
      <Helmet>
        <title>Water Level Records</title>
      </Helmet>

      {/* Page Header */}
      <Typography variant="h3" gutterBottom display="inline">
        Water Level Records
      </Typography>
      <Breadcrumbs sx={{ mt: 4 }} aria-label="Breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Water Level Records</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 6 }} />

      {/* Well Selection */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Water Level Entry
          </Typography>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, mt: 2 }}
          >
            <Autocomplete
              options={filteredWells}
              getOptionLabel={(option) => {
                const company = option.name_company || "No company listed";
                const hasName = option.name_first || option.name_last;
                const name = hasName
                  ? `${option.name_first ?? ""} ${
                      option.name_last ?? ""
                    }`.trim()
                  : "No name provided";
                const wellLabel = `Well #${option.well_ndx}`;
                const permitLabel = option.assoc_permit_ndx
                  ? `Permit #${option.assoc_permit_ndx}`
                  : null;

                return [wellLabel, permitLabel, company, name]
                  .filter(Boolean)
                  .join(" | ");
              }}
              value={wellsData.find((w) => w.well_ndx === wellNdx) || null}
              onChange={handleWellChange}
              renderInput={(params) => <TextField {...params} label="Wells" />}
              loading={isLoadingWells}
              fullWidth
              sx={{ maxWidth: 500 }}
            />
            <AdminVisibilityFilter>
              <Box sx={{ ml: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showAllWells}
                      onChange={() => setShowAllWells((prev) => !prev)}
                    />
                  }
                  label={
                    showAllWells
                      ? "Showing: All Wells"
                      : "Showing: Permitted Only"
                  }
                />
              </Box>
            </AdminVisibilityFilter>
          </Box>

          {/* Data Grid */}
          {wellNdx ? (
            <Box
              sx={{
                height: 600,
                width: "100%",
              }}
            >
              <DataGrid
                rows={waterLevelsData}
                columns={columns}
                loading={loading}
                getRowId={(row) => row.ndx}
                disableMultipleRowSelection
                rowSelectionModel={selectedRowId ? [selectedRowId] : []}
                onRowSelectionModelChange={handleRowSelection}
                slots={{ toolbar: CustomToolbar }}
                sx={{
                  "& .MuiDataGrid-cell:focus": { outline: "none" },
                  "& .MuiDataGrid-cell:focus-within": { outline: "none" },
                  "& .MuiDataGrid-row:hover": { cursor: "pointer" },
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "#d9e7f5",
                  },
                  "& .MuiDataGrid-row.Mui-selected:hover": {
                    backgroundColor: "#c3dbf2",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "normal",
                  },
                }}
              />
            </Box>
          ) : (
            <Alert severity="info" sx={{ my: 2 }}>
              Select a Well to view and update water-level entries.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Example top "cards" - Current Drought Status, Pumpage Summary */}
      <Box>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Contact Details
                </Typography>
                {!wellNdx ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Select a Well to see the Contact Details.
                  </Alert>
                ) : isLoadingContact ? (
                  <Box display="flex" justifyContent="center" my={2}>
                    <CircularProgress />
                  </Box>
                ) : isErrorContact ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load contact information.
                  </Alert>
                ) : contactData.length === 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No contact details found for this well.
                  </Alert>
                ) : (
                  <>
                    {/* Primary Contact */}
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Primary Contact
                    </Typography>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Company:</strong>{" "}
                          {contactData[0].name_company || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Name:</strong>{" "}
                          {`${contactData[0].name_first} ${contactData[0].name_last}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Phone:</strong>{" "}
                          {contactData[0].phone_primary || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Email:</strong>{" "}
                          {contactData[0].email || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Address:</strong>{" "}
                          {[
                            contactData[0].address,
                            contactData[0].city,
                            contactData[0].state,
                            contactData[0].zip_code,
                          ]
                            .filter(Boolean)
                            .join(", ") || "N/A"}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Divider sx={{ mb: 2 }} />

                    {/* Associations */}
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Associations
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Permits:</strong>
                        </Typography>
                        <List dense disablePadding>
                          {(contactData[0].permit_nicknames || []).map(
                            (name, idx) => {
                              const permitNdx =
                                contactData[0].permit_ndxs?.[idx];
                              const display = name
                                ? `${name} (#${permitNdx})`
                                : `Permit #${permitNdx}`;
                              return (
                                <ListItem key={idx}>
                                  <ListItemText primary={display} />
                                </ListItem>
                              );
                            }
                          )}
                        </List>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Wells:</strong>
                        </Typography>
                        <List dense disablePadding>
                          {(contactData[0].well_ndxs || []).map((well, idx) => (
                            <ListItem key={idx}>
                              <ListItemText primary={`#${well}`} />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    </Grid>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Map
                </Typography>
                <PageContainer>
                  <Map
                    data={wellsData}
                    visibleIds={filteredWells.map((w) => w.well_ndx)}
                  />
                </PageContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Dialog for Add/Edit */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isCreateMode ? "Add Water Level Entry" : "Edit Water Level Entry"}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <Box
              component="form"
              noValidate
              id="water-level-form"
              onSubmit={handleSubmit(onSubmitForm)}
              sx={{ mt: 1 }}
            >
              <Controller
                name="well_ndx"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />

              {/* Date Picker */}
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Date"
                    value={field.value}
                    onChange={field.onChange}
                    slotProps={{
                      textField: {
                        margin: "normal",
                        fullWidth: true,
                        error: !!errors.date,
                        helperText: errors.date?.message,
                        required: true,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="time"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="time"
                    label="Time"
                    margin="normal"
                    fullWidth
                    value={field.value ?? ""}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.time}
                    helperText={errors.time?.message}
                  />
                )}
              />

              <Controller
                name="land_surface_elevation_ftabsl"
                control={control}
                render={({ field }) => {
                  const coords = filteredWells.find(
                    (w) => w.well_ndx === wellNdx
                  )?.geometry?.coordinates;
                  const lat = coords?.[1];
                  const lon = coords?.[0];

                  const onElevationSuccess = (val) => {
                    setValue("land_surface_elevation_ftabsl", String(val), {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  };

                  return (
                    <TextField
                      {...field}
                      margin="normal"
                      label="Land Surface Elevation (ft abv sea lvl)"
                      fullWidth
                      type="number"
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!errors.land_surface_elevation_ftabsl}
                      helperText={errors.land_surface_elevation_ftabsl?.message}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <FetchElevationButton
                                lat={lat}
                                lon={lon}
                                onSuccess={onElevationSuccess}
                              />
                            </InputAdornment>
                          ),
                        },
                        htmlInput: {
                          step: "0.1",
                          inputMode: "decimal",
                          pattern: "^\\d+\\.\\d{1}$",
                        },
                      }}
                    />
                  );
                }}
              />

              {/* Numeric Input Fields */}
              {waterLevelFields.map(({ value, label, required }) => {
                // Special handling for water elevation field
                if (value === "water_elevation_ft_absl") {
                  const landSurfaceElevation = watch(
                    "land_surface_elevation_ftabsl"
                  );
                  const measuringPoint = watch("measuring_point_ft");
                  const waterLevelDepth = watch("water_level_ft_below_mp");

                  return (
                    <Controller
                      key={value}
                      name={value}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          margin="normal"
                          label={label}
                          fullWidth
                          type="number"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            field.onChange(val === "" ? null : parseFloat(val));
                          }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CalculateWaterElevationButton
                                    landSurfaceElevation={landSurfaceElevation}
                                    measuringPoint={measuringPoint}
                                    waterLevelDepth={waterLevelDepth}
                                    onCalculate={(calculatedValue) => {
                                      setValue(
                                        "water_elevation_ft_absl",
                                        calculatedValue,
                                        {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                          shouldTouch: true,
                                        }
                                      );
                                    }}
                                  />
                                </InputAdornment>
                              ),
                            },
                            htmlInput: {
                              step: "0.1",
                              inputMode: "decimal",
                              pattern: "^\\d+\\.\\d{1}$",
                            },
                          }}
                          error={!!errors[value]}
                          helperText={errors[value]?.message}
                          required={required}
                        />
                      )}
                    />
                  );
                }

                // Default rendering for other fields
                return (
                  <Controller
                    key={value}
                    name={value}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        margin="normal"
                        label={label}
                        fullWidth
                        type="number"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val === "" ? null : parseFloat(val));
                        }}
                        slotProps={{
                          htmlInput: {
                            step: "0.1",
                            inputMode: "decimal",
                            pattern: "^\\d+\\.\\d{1}$",
                          },
                        }}
                        error={!!errors[value]}
                        helperText={errors[value]?.message}
                        required={required}
                      />
                    )}
                  />
                );
              })}

              {/* Measurement Method */}
              <Controller
                name="measure_method_ndx"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={measurementMethodsData}
                    getOptionLabel={(option) => option.measure_method}
                    value={
                      measurementMethodsData.find(
                        (m) => m.measure_method_ndx === field.value
                      ) || null
                    }
                    onChange={(_, newVal) =>
                      field.onChange(newVal?.measure_method_ndx || null)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Measurement Method"
                        margin="normal"
                        error={!!errors.measure_method_ndx}
                        helperText={errors.measure_method_ndx?.message}
                        required
                      />
                    )}
                  />
                )}
              />

              {/* comments */}
              <Controller
                name="comments"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Other Comments"
                    fullWidth
                    multiline
                  />
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            form="water-level-form"
            type="submit"
            variant="contained"
            disabled={!isDirty || isSubmitting}
          >
            {isSubmitting ? "Saving..." : isCreateMode ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this Water Level record?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
